import { createContext, useContext, useRef, useState, useCallback } from "react";

const RadioContext = createContext(null);

export function RadioProvider({ children }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentStation, setCurrentStation] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [volume, setVolumeState] = useState(0.8);
  const [visible, setVisible] = useState(false);

  const playStation = useCallback(async (station, track) => {
    const url = track?.storageURL || station?.streamURL || null;
    if (!url) return;

    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    audioRef.current.pause();
    audioRef.current.src = url;
    audioRef.current.volume = volume;
    await audioRef.current.play().catch(console.error);
    setPlaying(true);
    setCurrentStation(station);
    setCurrentTrack(track || null);
    setVisible(true);
  }, [volume]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
  }, []);

  const resume = useCallback(async () => {
    await audioRef.current?.play().catch(console.error);
    setPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (playing) pause();
    else resume();
  }, [playing, pause, resume]);

  const setVolume = useCallback((v) => {
    setVolumeState(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.src = "";
    setPlaying(false);
    setCurrentStation(null);
    setCurrentTrack(null);
    setVisible(false);
  }, []);

  return (
    <RadioContext.Provider value={{ playing, currentStation, currentTrack, volume, visible, playStation, pause, resume, togglePlay, setVolume, stop }}>
      {children}
    </RadioContext.Provider>
  );
}

export function useRadio() {
  const ctx = useContext(RadioContext);
  if (!ctx) throw new Error("useRadio must be inside RadioProvider");
  return ctx;
}
